import { type ReportKpiRow } from '@terros/common'
import type { Dispatch, SetStateAction } from 'react'
import { type SortDirection } from './sort'
import z from 'zod'

export type ColumnId =
  | 'avatar'
  | 'name'
  | 'contactCount'
  | 'pitchCount'
  | 'pitchToLeadRatio'
  | 'daysSincePitch'
  | 'firstPitchOfDay'
  | 'lastPitchOfDay'
  | 'daysWorked'
  | 'eventSitsCount'
  | 'closePercent'
  | 'eventReschedulesCount'
  | 'setterLeadCount'
  | 'setterSetCount'
  | 'setterSitCount'
  | 'setterClosedCount'
  | 'setterApprovedCount'
  | 'setterInstalledCount'
  | 'setterSetToSitRatio'
  | 'closerSitToCloseRatio'
  | 'closerLeadCount'
  | 'closerSetCount'
  | 'closerSitCount'
  | 'closerClosedCount'
  | 'closerApprovedCount'
  | 'closerInstalledCount'
  | 'daysSinceClose'
  | 'pitchToSetRatio'
  | 'pitchToApprovedRatio'
  | 'installToCloseRatio'
  | 'setterArchivedCount'
  | 'closerArchivedCount'
  | 'closerArchiveToClosedRatio'
  | 'daysSinceLead'
  | 'daysSinceInstall'
  | 'overdueTasks'
  | 'meetingAttendance'
  | 'evaluationCount'
  | 'evaluationScore'
  | 'evaluatedCount'
  | 'evaluatedScore'
  | 'lastInterviewDate'
  | 'nextInterviewDate'
  | 'messageCount'
  | 'quarterlyTargetPitches'
  | 'quarterlyTargetLeads'
  | 'quarterlyTargetCloses'
  | 'quarterlyTargetInstalls'
  | 'weeklyTargetPitches'
  | 'daysSinceFirstAccess'
  | 'daysSinceLastAccess'
  | 'teamManagers'
  | 'userCount'

export type ClientKpiRow = ReportKpiRow & {
  avatar: Avatar
  name: string
  contactCount: number
  pitchCount: number
  pitchToLeadRatio: number
  pitchToLeadRatioDisplay: string
  pitchToSetRatio: number
  pitchToSetRatioDisplay: string
  setterLeadCount: number
  setterSetCount: number
  setterSitCount: number
  setterClosedCount: number
  setterApprovedCount: number
  setterInstalledCount: number
  setterArchivedCount: number
  setterSetToSitRatio: number
  setterSetToSitRatioDisplay: string
  closerLeadCount: number
  closerSetCount: number
  closerSitCount: number
  closerClosedCount: number
  closerApprovedCount: number
  closerInstalledCount: number
  closerArchivedCount: number
  closerSitToCloseRatio: number
  closerSitToCloseRatioDisplay: string
  closerArchiveToClosedRatio: number
  closerArchiveToClosedRatioDisplay: string
  eventSitsCount: number
  closePercent: number
  closePercentDisplay: string
  eventReschedulesCount: number
  pitchToApprovedRatio: number
  pitchToApprovedRatioDisplay: string
  installToCloseRatio: number
  installToCloseRatioDisplay: string
  firstPitchOfDay: number
  firstPitchOfDayDisplay: string
  lastPitchOfDay: number
  lastPitchOfDayDisplay: string
  daysSinceLead: number
  daysSincePitch: number
  daysSinceClose: number
  daysSinceInstall: number
  daysSinceFirstAccess: number
  daysSinceLastAccess: number
  daysSinceLeadDisplay: string
  daysSincePitchDisplay: string
  daysSinceCloseDisplay: string
  daysSinceInstallDisplay: string
  daysSinceFirstAccessDisplay: string
  daysSinceLastAccessDisplay: string
  daysWorked: number
  overdueTasks: number
  meetingAttendance: number
  evaluationCount: number
  evaluationScore: number
  evaluationScoreDisplay: string | number
  evaluatedCount: number
  evaluatedScore: number
  evaluatedScoreDisplay: string | number
  isSelf?: boolean
  lastInterviewDate: number
  lastInterviewDateDisplay: string
  nextInterviewDate: number
  nextInterviewDateDisplay: string
  messageCount: number
  quarterlyTargetPitches: number
  quarterlyTargetLeads: number
  quarterlyTargetCloses: number
  quarterlyTargetInstalls: number
  weeklyTargetPitches: number
  teamManagers: string
  userCount: number
}

export type Avatar = {
  initials: string
  avatarBlurhash?: string
  avatarUrl?: string
}

export type FilterState = Filter[]

export type StringFilter = string
export const NumberRangeFilterSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
})
export type NumberRangeFilter = z.infer<typeof NumberRangeFilterSchema>

export type ColumnFilter = StringFilter | NumberRangeFilter

export type FilterStateV2 = Record<string, ColumnFilter>

export type ColumnFilterConsumer = {
  filter: ColumnFilter | undefined
  setFilter: Dispatch<SetStateAction<ColumnFilter | undefined>>
}

export type SortState = {
  column: string
  dir: SortDirection
}

export type Filter = {
  column: ColumnId
  min?: number
  max?: number
}
